import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../../../Common/SidebarPlugin/SidebarContext';
import { resolveWorkspaceMode } from '../workspace/workspaceMode';
import { useAiPageContext } from '../workspace/useAiPageContext';
import { trackWorkspaceEvent } from '../workspace/telemetry';
import type { RootState } from '../../../../../redux/store';
import {
  useCreateAiConversationMutation,
  useLazyListAiConversationMessagesQuery,
  useListAiConversationsQuery,
  useUpdateAiConversationMutation,
} from '../infrastructure/ai-conversations.api';
import {
  type AiConversationStreamEvent,
  streamAiConversationMessage,
} from '../infrastructure/ai-conversation-stream';
import type { AiResourceTag } from '../contracts/ai-context.types';
import type { AiPromptSelection } from '../contracts/ai-prompt.types';
import type {
  PresentationDocument,
  PresentationDocumentMetaEvent,
  PresentationNodeEvent,
} from '../contracts/ai-presentation.types';
import { PRESENTATION_SCHEMA_VERSION } from '../presentation/constants';
import {
  resolveBackendNotice,
  strictTranslate,
} from '../localization';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  presentation?: PresentationDocument;
}

const isPresentationNodeEvent = (
  event: AiConversationStreamEvent
): event is PresentationNodeEvent =>
  event.type === 'assistant.node.created' ||
  event.type === 'assistant.node.updated' ||
  event.type === 'assistant.node.completed';

const isPresentationDocumentMetaEvent = (
  event: AiConversationStreamEvent
): event is PresentationDocumentMetaEvent =>
  event.type === 'assistant.document.meta';

const MAX_CONVERSATION_TITLE_TEXT_LENGTH = 55;

/**
 * Builds a conversation title from the first user message.
 * Title is stored data — not a localized UI label — so ISO date format is used:
 * locale-independent, searchable, and sortable.
 *
 * Exported for unit testing.
 */
export function buildConversationTitle(message: string, conversationId: number): string {
  const date = new Date().toISOString().slice(0, 10);
  const text = message.trim();

  if (!text) {
    // Defensive fallback for an unexpected empty first message.
    // This is stored conversation data, not a localized UI label.
    return `Conversation ${conversationId}`;
  }

  const truncated =
    text.length > MAX_CONVERSATION_TITLE_TEXT_LENGTH
      ? `${text.slice(0, MAX_CONVERSATION_TITLE_TEXT_LENGTH)}…`
      : text;

  return `${date} — ${truncated}`;
}

export const useAskAISidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { close: closeSidebar, isExpanded, displayMode, setDisplayMode, toggleExpanded } = useSidebar();
  const mode = resolveWorkspaceMode(displayMode, isExpanded);
  const clientContext = useAiPageContext();
  const uiLocale = clientContext.locale ?? 'en';
  const translated = (key: string) => strictTranslate(t, key, uiLocale);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [messageErrorCode, setMessageErrorCode] = useState<'provider_unavailable' | null>(null);
  const [resourceWarning, setResourceWarning] = useState<string | null>(null);
  const [selectedPrompt, setSelectedPrompt] =
    useState<AiPromptSelection | null>(null);
  const [isAnalyzingInventory, setIsAnalyzingInventory] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  /** Holds the last user-sent payload so `ErrorLayout` can re-trigger it. */
  const lastSendPayloadRef = useRef<{
    message: string;
    resourceTags: AiResourceTag[];
    prompt: AiPromptSelection | null;
  } | null>(null);
  const [isSwitchingConversation, setIsSwitchingConversation] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Per-message, per-node last-applied sequence, so an out-of-order or
  // duplicate SSE frame is never applied over a newer one.
  const nodeSequencesRef = useRef<Record<string, Record<string, number>>>({});
  const accessToken = useSelector(
    (state: RootState) => state.user.info.accessToken
  );
  const userRole = useSelector((state: RootState) => state.user.info.role);
  const [createConversation, { isLoading: isCreatingConversation }] =
    useCreateAiConversationMutation();
  const [updateConversation] = useUpdateAiConversationMutation();
  // The dropdown only ever shows a handful of recent conversations; the
  // expanded Rail is a full date-grouped history, so it asks for more.
  const { data: conversations, isFetching: isLoadingConversations } =
    useListAiConversationsQuery(
      { limit: isExpanded ? 50 : 5 },
      { skip: !isHistoryOpen && !isExpanded }
    );
  const [fetchConversationMessages] = useLazyListAiConversationMessagesQuery();
  const [activeConversationTitle, setActiveConversationTitle] = useState<
    string | null
  >(null);
  const hasActiveConversation = conversationId !== null;
  const isShowingWelcomeState =
    !isSwitchingConversation && !hasActiveConversation;
  const conversationLabel = hasActiveConversation
    ? activeConversationTitle ||
      t('ai.conversation-number', { id: conversationId })
    : t('ai.new-conversation');

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    if (!accessToken) closeSidebar();
  }, [accessToken, closeSidebar]);

  /**
   * Generates and PATCHes the conversation title from the first user message.
   * currentTitle must be passed explicitly — never read from closure — to avoid
   * stale React state in Path A (newly created conversation).
   * No-op when currentTitle is already non-empty. Always best-effort: failure
   * does not affect message sending.
   */
  const autoTitleConversation = (
    id: number,
    message: string,
    currentTitle: string | null,
  ) => {
    if (currentTitle) return;

    const autoTitle = buildConversationTitle(message, id);
    setActiveConversationTitle(autoTitle);
    void updateConversation({ conversationId: id, title: autoTitle })
      .unwrap()
      .catch(() => { /* best-effort, message send is unaffected */ });
  };

  const handleInputValueChange = (value: string) => {
    if (!value.trim()) {
      setSelectedPrompt(null);
    }
    setInputValue(value);
  };

  const handleCreateConversation = () => {
    setCreateError(null);
    setSelectedPrompt(null);
    setIsAnalyzingInventory(false);
    abortControllerRef.current?.abort();

    createConversation()
      .unwrap()
      .then((conversation) => {
        setConversationId(conversation.id);
        setActiveConversationTitle(conversation.title ?? null);
        setMessages([]);
        // Same rule as `handleSelectConversation`: starting a new
        // conversation must not drop a draft still sitting in the
        // composer — only actually sending a message clears it (see
        // `handleSend`).
        setMessageError(null);
        setMessageErrorCode(null);
      })
      .catch(() => {
        setCreateError(translated('ai.errors.create-failed'));
      });
  };

  const handleSelectConversation = (id: number) => {
    if (id === conversationId) return;

    abortControllerRef.current?.abort();
    setIsStreaming(false);
    setCreateError(null);
    setMessageError(null);
    setMessageErrorCode(null);
    setSelectedPrompt(null);
    setIsAnalyzingInventory(false);
    setIsSwitchingConversation(true);
    nodeSequencesRef.current = {};

    fetchConversationMessages(id)
      .unwrap()
      .then((history) => {
        setConversationId(id);
        setActiveConversationTitle(
          conversations?.find((conversation) => conversation.id === id)
            ?.title ?? null
        );
        // Deliberately NOT clearing `inputValue` here: switching to a
        // different conversation while the composer already has a
        // draft should keep that draft — only actually sending a
        // message clears it (see `handleSend`). This only needs to
        // hold while the sidebar stays mounted; closing/reopening it
        // resets all local state anyway.
        setMessages(
          history.map((message) => ({
            id: `${message.role}-${message.id}`,
            role: message.role,
            content: message.content ?? '',
            ...(message.presentation
              ? { presentation: message.presentation }
              : {}),
          }))
        );
      })
      .catch(() => {
        setMessageError(translated('ai.errors.load-failed'));
      })
      .finally(() => {
        setIsSwitchingConversation(false);
      });
  };

  const sendMessageToConversation = (
    targetConversationId: number,
    message: string,
    resourceTags: AiResourceTag[],
    prompt: AiPromptSelection | null
  ): Promise<boolean> => {
    setIsStreaming(true);
    setIsAnalyzingInventory(false);
    setResourceWarning(null);
    const userMessageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    nodeSequencesRef.current = {};
    setMessages((current) => [
      ...current,
      { id: userMessageId, role: 'user', content: message },
      { id: assistantMessageId, role: 'assistant', content: '' },
    ]);

    trackWorkspaceEvent('WorkspaceOpened', { conversationId: targetConversationId });

    return streamAiConversationMessage({
      conversationId: targetConversationId,
      content: message,
      promptId: prompt?.id,
      promptVersion: prompt?.version,
      resourceTags: resourceTags.map(({ kind, id }) => ({ kind, id })),
      clientContext,
      accessToken,
      signal: abortController.signal,
      onEvent: (event: AiConversationStreamEvent) => {
        if (event.type === 'assistant.created') {
          setMessages((current) =>
            current.map((item) =>
              item.id === assistantMessageId
                ? { ...item, id: `assistant-${event.message.id}` }
                : item
            )
          );
          return;
        }

        if (event.type === 'assistant.delta') {
          setMessages((current) =>
            current.map((item) =>
              item.id === `assistant-${event.messageId}`
                ? { ...item, content: item.content + event.delta }
                : item
            )
          );
          return;
        }

        if (event.type === 'assistant.completed') {
          setIsAnalyzingInventory(false);
          setMessages((current) =>
            current.map((item) =>
              item.id === `assistant-${event.message.id}`
                ? { ...item, content: event.message.content }
                : item
            )
          );
          return;
        }

        if (event.type === 'tool.started' || event.type === 'tool.progress') {
          setIsAnalyzingInventory(true);
          return;
        }

        if (event.type === 'tool.completed') {
          setIsAnalyzingInventory(false);
          return;
        }

        if (event.type === 'tool.failed') {
          setIsAnalyzingInventory(false);
          const toolError = event.error ?? event.message;
          if (toolError) {
            setMessageError(
              resolveBackendNotice(
                toolError,
                null,
                uiLocale,
                t,
                'ai.errors.tool-failed',
              ),
            );
            setMessages((current) =>
              current.filter(
                (m) =>
                  m.id !== assistantMessageId &&
                  (!event.messageId || m.id !== `assistant-${event.messageId}`)
              )
            );
          }
          return;
        }

        if (event.type === 'context.warning') {
          setResourceWarning(event.message);
          return;
        }

        if (event.type === 'run.failed') {
          setIsAnalyzingInventory(false);
          setMessageError(
            resolveBackendNotice(
              event.error,
              event.errorCode,
              uiLocale,
              t,
              'ai.errors.send-failed',
            ),
          );
          setMessageErrorCode(event.errorCode ?? null);
          setMessages((current) =>
            current.filter(
              (m) =>
                m.id !== assistantMessageId &&
                (!event.messageId || m.id !== `assistant-${event.messageId}`)
            )
          );
          return;
        }

        if (isPresentationDocumentMetaEvent(event)) {
          const messageKey = `assistant-${event.messageId}`;
          setMessages((current) =>
            current.map((item) =>
              item.id === messageKey
                ? {
                    ...item,
                    presentation: {
                      schemaVersion: PRESENTATION_SCHEMA_VERSION,
                      messageId: event.messageId,
                      layout: event.layout,
                      responseMode: event.responseMode,
                      ...(event.densityHint
                        ? { densityHint: event.densityHint }
                        : {}),
                      nodes: item.presentation?.nodes ?? [],
                      generatedAt: new Date().toISOString(),
                    },
                  }
                : item
            )
          );
          return;
        }

        if (isPresentationNodeEvent(event)) {
          const messageKey = `assistant-${event.messageId}`;
          const nodeId = event.node.meta.id;
          const lastSequence =
            nodeSequencesRef.current[messageKey]?.[nodeId] ?? -1;
          if (event.sequence <= lastSequence) return;
          nodeSequencesRef.current[messageKey] = {
            ...(nodeSequencesRef.current[messageKey] ?? {}),
            [nodeId]: event.sequence,
          };

          setMessages((current) =>
            current.map((item) => {
              if (item.id !== messageKey) return item;
              const existingDocument = item.presentation;
              const existingNodes = existingDocument?.nodes ?? [];
              const existingIndex = existingNodes.findIndex(
                (node) => node.meta.id === nodeId
              );
              const nextNodes =
                existingIndex >= 0
                  ? existingNodes.map((node, index) =>
                      index === existingIndex ? event.node : node
                    )
                  : [...existingNodes, event.node];
              return {
                ...item,
                presentation: existingDocument
                  ? { ...existingDocument, nodes: nextNodes }
                  : {
                      schemaVersion: PRESENTATION_SCHEMA_VERSION,
                      messageId: event.messageId,
                      layout: 'stack',
                      responseMode: 'prefer_presentation',
                      nodes: nextNodes,
                      generatedAt: new Date().toISOString(),
                    },
              };
            })
          );
        }
      },
    })
      .then(() => true)
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setMessageError(translated('ai.errors.send-failed'));
          setMessages((current) => current.filter((m) => m.id !== assistantMessageId));
        }
        return false;
      })
      .finally(() => {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        setIsAnalyzingInventory(false);
        setIsStreaming(false);
      });
  };

  /**
   * Applies the result of a manual "Refresh" presentation action (a plain
   * REST call, no SSE/LLM involved) to the matching assistant bubble.
   */
  const handlePresentationRefreshed = (
    assistantMessageId: number,
    presentation: PresentationDocument
  ) => {
    setMessages((current) =>
      current.map((item) =>
        item.id === `assistant-${assistantMessageId}`
          ? { ...item, presentation }
          : item
      )
    );
  };

  const handleUsePrompt = (prompt: AiPromptSelection) => {
    setSelectedPrompt(prompt);
    if (conversationId) {
      setInputValue(prompt.text);
      return;
    }

    setCreateError(null);
    createConversation()
      .unwrap()
      .then((conversation) => {
        setConversationId(conversation.id);
        setActiveConversationTitle(conversation.title ?? null);
        setMessages([]);
        setInputValue(prompt.text);
        setMessageError(null);
      })
      .catch(() => {
        setInputValue(prompt.text);
        setCreateError(translated('ai.errors.create-failed'));
      });
  };

  /**
   * Re-sends the last message — wired to `ErrorLayout`'s Retry button.
   * Only retries if there's a stored payload and an active conversation.
   */
  const handleRetry = () => {
    if (!lastSendPayloadRef.current || !conversationId) return;
    const { message, resourceTags, prompt } = lastSendPayloadRef.current;
    setMessageError(null);
    setMessageErrorCode(null);
    setCreateError(null);
    sendMessageToConversation(conversationId, message, resourceTags, prompt);
  };

  const handleSend = (resourceTags: AiResourceTag[] = []) => {
    const message = inputValue.trim();
    if (!message || isStreaming) return;
    const prompt = selectedPrompt;

    setMessageError(null);
    setMessageErrorCode(null);
    setInputValue('');
    lastSendPayloadRef.current = { message, resourceTags, prompt };

    // No active conversation yet (welcome/default state) — create one on the fly
    // and send the pending message straight into it, instead of blocking the user.
    if (!conversationId) {
      setCreateError(null);
      createConversation()
        .unwrap()
        .then((conversation) => {
          setConversationId(conversation.id);
          setActiveConversationTitle(conversation.title ?? null);
          // Path A: title comes from the fresh server response, not stale state.
          autoTitleConversation(conversation.id, message, conversation.title ?? null);
          sendMessageToConversation(
            conversation.id,
            message,
            resourceTags,
            prompt
          ).then((wasSuccessful) => {
            if (wasSuccessful) {
              setSelectedPrompt((current) =>
                current === prompt ? null : current
              );
            } else {
              setInputValue(message);
            }
          });
        })
        .catch(() => {
          setInputValue(message);
          setCreateError(translated('ai.errors.create-failed'));
        });
      return;
    }

    // Path B: conversation pre-created by handleUsePrompt.
    // activeConversationTitle is synchronized with conversationId by the
    // existing conversation-switch logic (handleSelectConversation etc.).
    autoTitleConversation(conversationId, message, activeConversationTitle);
    sendMessageToConversation(
      conversationId,
      message,
      resourceTags,
      prompt
    ).then((wasSuccessful) => {
      if (wasSuccessful) {
        setSelectedPrompt((current) => (current === prompt ? null : current));
      } else {
        setInputValue(message);
      }
    });
  };

  return {
    t,
    inputValue,
    setInputValue: handleInputValueChange,
    messages,
    conversationId,
    createError,
    messageError,
    messageErrorCode,
    resourceWarning,
    isAnalyzingInventory,
    isStreaming,
    isHistoryOpen,
    setIsHistoryOpen,
    isSwitchingConversation,
    conversations,
    isLoadingConversations,
    isCreatingConversation,
    conversationLabel,
    hasActiveConversation,
    isShowingWelcomeState,
    handleCreateConversation,
    handleSelectConversation,
    handleUsePrompt,
    handlePresentationRefreshed,
    handleSend,
    userRole,
    closeSidebar,
    abortControllerRef,
    navigate,
    displayMode,
    setDisplayMode,
    toggleExpanded,
    isExpanded,
    mode,
    handleRetry,
  };
};
